// src/pages/Assistant.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Target, TrendingUp, Plus, Trash2, Euro, Users, Loader2, MessageSquare, ChevronRight, Pencil } from 'lucide-react';
import { toast } from 'sonner';

import MarkdownLite from '@/components/ui/MarkdownLite';
import DatePicker from '@/components/ui/DatePicker';
import Modal from '@/components/ui/Modal';
import PrimaryButton from '@/components/ui/PrimaryButton';
import AssistantChat from '@/components/assistant/AssistantChat';
import GoalFormModal, { NewGoalPayload } from '@/components/assistant/GoalFormModal';
import { computeSalonTrends } from '@/services/assistant/salonContextService';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useEvents } from '@/contexts/EventsContext';
import { useClients } from '@/contexts/ClientsContext';
import { useUsers } from '@/contexts/UsersContext';
import { useAnalytics } from '@/contexts/AnalyticsContext';
import {
    fetchGoalsService,
    createGoalService,
    updateGoalService,
    deleteGoalService,
    fetchGoalChecksService,
    createGoalCheckService,
    nextCheckDateAfter,
    planTotalAmount,
    monthsForRata,
    rataForMonths,
    Goal,
    GoalCheck,
} from '@/services/goals/goalsService';
import {
    fetchCompensationsService,
    monthlyCostFromCompensation,
    EmployeeCompensation,
} from '@/services/employees/employeeCompensationService';
import {
    fetchShopCostsService,
    monthlyizeCost,
    ShopCost,
} from '@/services/shop/shopCostsService';

const euro = (n: number) => `€ ${Math.round(n).toLocaleString('it-IT')}`;

type PlanProposal = {
    fattibile?: string;
    analisi?: string;
    durata_mesi?: number | null;
    rata_mensile_eur?: number | null;
    quota_margine_pct?: number | null;
    consigli?: string[];
};

// Parametri del piano in modifica (prima dell'accettazione).
type PlanEdit = { final_price: string; monthly: string; interest: string; months: string; start: string };

const Assistant: React.FC = () => {
    const { organization } = useAuth();
    const { bookings, busySlots, getPerformanceById, isLoading: eventsLoading } = useEvents();
    const { clients } = useClients();
    const { users } = useUsers();
    const { getValidBookings, calculateRevenue } = useAnalytics();

    const [activeTab, setActiveTab] = useState<'chat' | 'goals'>('chat');
    const [isNewGoalOpen, setIsNewGoalOpen] = useState(false);
    const [editingGoal, setEditingGoal] = useState<Goal | null>(null);
    const [selectedGoalId, setSelectedGoalId] = useState<string | null>(null);

    const [goals, setGoals] = useState<Goal[]>([]);
    const [comps, setComps] = useState<EmployeeCompensation[]>([]);
    const [costs, setCosts] = useState<ShopCost[]>([]);

    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    // Analisi/piano AI per obiettivo
    const [goalPlans, setGoalPlans] = useState<Record<string, { loading: boolean; plan?: PlanProposal; text?: string }>>({});
    // Parametri del piano in modifica (prima dell'accettazione)
    const [planEdit, setPlanEdit] = useState<Record<string, PlanEdit | undefined>>({});
    const [checks, setChecks] = useState<GoalCheck[]>([]);

    // ===== Trend calcolati dai dati reali (ultimi 6 mesi) =====
    const trends = useMemo(
        () => computeSalonTrends(bookings || [], clients || [], { getValidBookings, calculateRevenue }),
        [bookings, clients, getValidBookings, calculateRevenue],
    );

    // Situazione economica di sintesi (margine = fatturato − costi fissi − compensi).
    const financials = useMemo(() => {
        const fatturato = Math.round(trends.avgMonthlyRevenue);
        const costiFissi = Math.round(costs.reduce((s, c) => s + monthlyizeCost(c), 0));
        const compensi = Math.round(comps.reduce((s, c) => s + monthlyCostFromCompensation(c), 0));
        return { fatturato, costiFissi, compensi, margine: fatturato - costiFissi - compensi };
    }, [trends, costs, comps]);

    // Il contesto dati del salone è ora costruito lato server (edge function salon-assistant),
    // che legge direttamente tutte le tabelle del salone: non serve più calcolarlo qui.

    const loadGoals = async () => {
        if (!organization?.id) return;
        setLoading(true);
        const [res, checksRes] = await Promise.all([
            fetchGoalsService(Number(organization.id)),
            fetchGoalChecksService(Number(organization.id)),
        ]);
        if (res.success) setGoals(res.data);
        if (checksRes.success) setChecks(checksRes.data);
        setLoading(false);
    };

    // Compensi e costi salone: servono all'AI per margine, sostenibilità e break-even.
    const [financialsLoaded, setFinancialsLoaded] = useState(false);
    const loadFinancials = async () => {
        if (!organization?.id) return;
        const [compRes, costRes] = await Promise.all([
            fetchCompensationsService(Number(organization.id)),
            fetchShopCostsService(Number(organization.id)),
        ]);
        if (compRes.success) setComps(compRes.data);
        if (costRes.success) setCosts(costRes.data);
        setFinancialsLoaded(true);
    };

    useEffect(() => {
        loadGoals();
        loadFinancials();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organization?.id]);

    const handleCreate = async (payload: NewGoalPayload) => {
        if (!organization?.id) return;
        setIsSaving(true);
        const res = await createGoalService({
            shop_id: Number(organization.id),
            type: payload.type,
            title: payload.title,
            target: payload.target,
            deadline: payload.deadline,
            note: null,
            description: payload.description,
        });
        if (res.success) {
            toast.success('Obiettivo creato');
            setIsNewGoalOpen(false);
            await loadGoals();
        } else {
            toast.error(res.error || 'Errore nel salvataggio');
        }
        setIsSaving(false);
    };

    // Analisi AI dell'obiettivo (mode goal_plan): piano strutturato mirato al tipo di obiettivo.
    // Il risultato viene PERSISTITO sull'obiettivo (ai_analysis) e mostrato sempre, senza click.
    const analyzeGoal = async (g: Goal) => {
        setGoalPlans((p) => ({ ...p, [g.id]: { loading: true } }));
        const targetStr = g.type === 'economic' ? euro(g.target) : `${g.target} clienti`;
        const query =
            `Tipo: ${g.type === 'economic' ? 'economico (spesa/investimento)' : 'acquisizione clienti'}\n` +
            `Titolo: ${g.title || '—'}\n` +
            `Descrizione: ${g.description || '—'}\n` +
            `Valore: ${targetStr}${g.deadline ? `\nScadenza desiderata: ${new Date(g.deadline).toLocaleDateString('it-IT')}` : ''}`;
        try {
            const { data, error } = await supabase.functions.invoke('salon-assistant', {
                body: { query, mode: 'goal_plan' },
            });
            if (error) throw error;
            setGoalPlans((p) => ({ ...p, [g.id]: { loading: false, plan: data?.plan, text: data?.text } }));
            if (data?.plan) {
                const analyzedAt = new Date().toISOString();
                await updateGoalService(g.id, { ai_analysis: data.plan, ai_analyzed_at: analyzedAt });
                setGoals((prev) => prev.map((x) => (x.id === g.id ? { ...x, ai_analysis: data.plan, ai_analyzed_at: analyzedAt } : x)));
            }
        } catch (_e) {
            setGoalPlans((p) => ({ ...p, [g.id]: { loading: false, text: "Errore nel contattare l'assistente. Riprova tra poco." } }));
        }
    };

    // Auto-analisi: appena un obiettivo esiste senza analisi né piano accettato, l'AI lo valuta da sola.
    const autoAnalyzedRef = useRef<Set<string>>(new Set());
    useEffect(() => {
        // Aspetta che prenotazioni, compensi e costi siano caricati: l'analisi deve vedere il quadro completo.
        if (loading || !financialsLoaded || eventsLoading) return;
        goals.forEach((g) => {
            if (!g.plan_accepted_at && !g.ai_analysis && !autoAnalyzedRef.current.has(g.id)) {
                autoAnalyzedRef.current.add(g.id);
                analyzeGoal(g);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [goals, loading, financialsLoaded, eventsLoading]);

    // Modifica dei parametri dell'obiettivo: salva e rigenera l'analisi AI sui nuovi dati.
    const handleEditGoal = async (payload: NewGoalPayload) => {
        if (!editingGoal) return;
        setIsSaving(true);
        const res = await updateGoalService(editingGoal.id, {
            type: payload.type,
            title: payload.title,
            target: payload.target,
            deadline: payload.deadline,
            description: payload.description,
        });
        if (res.success && res.data) {
            const updated = res.data;
            setGoals((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
            setEditingGoal(null);
            toast.success('Obiettivo aggiornato: rigenero l\'analisi');
            // Forza una nuova analisi AI sui parametri modificati.
            autoAnalyzedRef.current.delete(updated.id);
            analyzeGoal(updated);
        } else {
            toast.error(res.error || 'Errore nel salvataggio');
        }
        setIsSaving(false);
    };

    const toIso = (d: Date) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const todayIso = () => toIso(new Date());

    // Mesi tra inizio e scadenza (interi, mese parziale arrotondato per eccesso, min 1). 0 se la scadenza precede l'inizio.
    const monthsBetweenDates = (startIso: string, endIso: string): number => {
        const s = new Date(`${startIso}T00:00:00`);
        const e = new Date(`${endIso}T00:00:00`);
        if (isNaN(s.getTime()) || isNaN(e.getTime()) || e <= s) return 0;
        let months = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth());
        if (e.getDate() > s.getDate()) months += 1;
        return Math.max(1, months);
    };

    // Totale da coprire per un piano: prezzo+interessi (economico) o n° clienti (clienti).
    const planTotalFor = (g: Goal, e: PlanEdit): number =>
        g.type === 'economic'
            ? planTotalAmount(parseFloat(e.final_price) || 0, parseFloat(e.interest) || 0)
            : g.target || 0;

    // Arrotonda la rata secondo il tipo (euro interi / clienti interi, sempre per eccesso a copertura).
    const roundMonthly = (g: Goal, rata: number): number => (g.type === 'economic' ? Math.round(rata) : Math.ceil(rata));

    // Deriva la rata dai mesi correnti (la durata comanda, la rata si adegua).
    const withRataFromMonths = (g: Goal, e: PlanEdit): PlanEdit => {
        const rata = rataForMonths(planTotalFor(g, e), parseInt(e.months) || 0);
        return rata > 0 ? { ...e, monthly: String(roundMonthly(g, rata)) } : e;
    };

    // Ricalcola i campi collegati quando l'utente ne modifica uno: importo ↔ rata ↔ durata restano coerenti.
    // La durata è guidata da inizio+scadenza; la rata si adegua. Se modifico la rata a mano, sono i mesi ad adattarsi.
    const recomputePlan = (g: Goal, prev: PlanEdit, field: keyof PlanEdit, value: string): PlanEdit => {
        let next: PlanEdit = { ...prev, [field]: value };
        // Cambiando l'inizio, con una scadenza fissata la durata si ricalcola e la rata segue.
        if (field === 'start') {
            if (g.deadline && next.start) {
                const m = monthsBetweenDates(next.start, g.deadline);
                if (m > 0) next.months = String(m);
            }
            return withRataFromMonths(g, next);
        }
        // Override manuale della rata → adatto la durata.
        if (field === 'monthly') {
            const months = monthsForRata(planTotalFor(g, next), parseFloat(next.monthly) || 0);
            if (months > 0) next.months = String(months);
            return next;
        }
        // Mesi, prezzo o interessi: la durata comanda, la rata si adegua.
        return withRataFromMonths(g, next);
    };

    const updatePlanField = (g: Goal, field: keyof PlanEdit, value: string) =>
        setPlanEdit((p) => (p[g.id] ? { ...p, [g.id]: recomputePlan(g, p[g.id]!, field, value) } : p));

    // Apre il form di configurazione del piano.
    // La durata è calcolata in automatico da inizio→scadenza dell'obiettivo; la rata ne discende.
    const openPlanEditor = (g: Goal) => {
        const prop = goalPlans[g.id]?.plan ?? (g.ai_analysis as unknown as PlanProposal | null);
        const isEconomic = g.type === 'economic';
        const interest = g.plan_interest_rate ?? 7;
        const finalPrice = g.plan_final_price ?? (isEconomic ? g.target : 0);
        const total = isEconomic ? planTotalAmount(finalPrice, interest) : g.target || 0;
        const start = g.plan_start_date || todayIso();

        // Durata: prima dalle date (inizio→scadenza), poi dal piano salvato, infine dalla proposta AI.
        let months = g.deadline ? monthsBetweenDates(start, g.deadline) : 0;
        if (!months) months = g.plan_duration_months ?? prop?.durata_mesi ?? 0;

        // Rata: derivata dalla durata; se la durata manca, ripiega sulla rata salvata/proposta e da lì ricava i mesi.
        let monthly = months > 0 ? rataForMonths(total, months) : 0;
        if (!monthly) {
            monthly = g.plan_monthly_amount ?? prop?.rata_mensile_eur ?? 0;
            if (monthly > 0) months = monthsForRata(total, monthly);
        }

        setPlanEdit((p) => ({
            ...p,
            [g.id]: {
                final_price: String(isEconomic ? finalPrice : ''),
                monthly: monthly > 0 ? String(roundMonthly(g, monthly)) : '',
                interest: String(interest),
                months: months > 0 ? String(months) : '',
                start,
            },
        }));
    };

    const savePlan = async (g: Goal) => {
        const e = planEdit[g.id];
        if (!e) return;
        const months = parseInt(e.months);
        const monthly = parseFloat(e.monthly);
        if (!months || months <= 0 || !monthly || monthly <= 0) {
            toast.error('Inserisci durata e rata mensile valide');
            return;
        }
        const res = await updateGoalService(g.id, {
            plan_final_price: parseFloat(e.final_price) || null,
            plan_monthly_amount: monthly,
            plan_interest_rate: parseFloat(e.interest) || 0,
            plan_duration_months: months,
            plan_cadence: 'monthly',
            plan_start_date: e.start,
            plan_accepted_at: new Date().toISOString(),
            next_check_date: nextCheckDateAfter(e.start),
            status: 'active',
        });
        if (res.success) {
            toast.success('Piano accettato: riceverai una verifica a ogni scadenza');
            setPlanEdit((p) => ({ ...p, [g.id]: undefined }));
            await loadGoals();
        } else {
            toast.error(res.error || 'Errore nel salvataggio del piano');
        }
    };

    // Riapre un piano già attivo: torna alla vista con l'analisi AI per modificare i parametri e riattivarlo.
    const reopenPlan = async (g: Goal) => {
        const res = await updateGoalService(g.id, { plan_accepted_at: null, next_check_date: null, status: 'active' });
        if (res.success) {
            toast.success('Piano riaperto: modifica i parametri e riattivalo');
            setPlanEdit((p) => ({ ...p, [g.id]: undefined }));
            await loadGoals();
        } else {
            toast.error(res.error || 'Errore nella riapertura del piano');
        }
    };

    // Check-in periodico: l'utente conferma se ha rispettato l'impegno del periodo.
    const confirmCheck = async (g: Goal, status: 'done' | 'missed') => {
        if (!organization?.id || !g.next_check_date) return;
        const periodEnd = g.next_check_date;
        const periodStart = g.plan_start_date && periodEnd === nextCheckDateAfter(g.plan_start_date) ? g.plan_start_date : null;
        const res = await createGoalCheckService({
            goal_id: g.id,
            shop_id: Number(organization.id),
            period_start: periodStart,
            period_end: periodEnd,
            status,
        });
        if (!res.success) {
            toast.error(res.error || 'Errore nella conferma');
            return;
        }
        // Avanza alla prossima scadenza; se il piano è completato, chiude l'obiettivo.
        const doneCount = checks.filter((c) => c.goal_id === g.id).length + 1;
        const finished = g.plan_duration_months != null && doneCount >= g.plan_duration_months;
        await updateGoalService(g.id, finished
            ? { next_check_date: null, status: 'completed' }
            : { next_check_date: nextCheckDateAfter(periodEnd) });
        toast.success(status === 'done' ? 'Verifica confermata' : 'Registrato: periodo non rispettato');
        await loadGoals();
    };

    const handleDelete = async (id: string) => {
        const res = await deleteGoalService(id);
        if (res.success) {
            setGoals((prev) => prev.filter((g) => g.id !== id));
            if (selectedGoalId === id) setSelectedGoalId(null);
        } else {
            toast.error('Errore eliminazione');
        }
    };

    // Proiezione locale (senza AI) per un obiettivo
    const projectionFor = (g: Goal): { label: string; detail: string; tone: 'good' | 'warn' | 'neutral' } => {
        if (g.type === 'economic') {
            if (financials.fatturato <= 0) {
                return { label: 'Dati insufficienti', detail: 'Servono più dati di fatturato per una stima.', tone: 'neutral' };
            }
            const m = financials.margine;
            const costiInfo = `fatturato ${euro(financials.fatturato)} − costi salone ${euro(financials.costiFissi)} − compensi ${euro(financials.compensi)}`;
            if (m <= 0) {
                return {
                    label: 'Non sostenibile con i margini attuali',
                    detail: `Il margine mensile stimato è ${euro(m)} (${costiInfo}). Con i costi attuali questo obiettivo non è finanziabile: servono più ricavi o meno costi.`,
                    tone: 'warn',
                };
            }
            const months = g.target / m;
            let detail = `Col margine mensile di ${euro(m)} (${costiInfo}) copri ${euro(g.target)} in circa ${months.toFixed(1)} mesi.`;
            let tone: 'good' | 'warn' | 'neutral' = months <= 12 ? 'good' : 'warn';
            if (g.deadline) {
                const monthsToDeadline = (new Date(g.deadline).getTime() - Date.now()) / (1000 * 60 * 60 * 24 * 30);
                if (monthsToDeadline > 0) {
                    const ok = months <= monthsToDeadline;
                    tone = ok ? 'good' : 'warn';
                    detail += ` Entro la scadenza (~${monthsToDeadline.toFixed(1)} mesi) ${ok ? 'è raggiungibile' : 'il ritmo attuale non basta'}.`;
                }
            }
            return { label: months <= 12 ? 'Sostenibile' : 'Obiettivo impegnativo', detail, tone };
        }
        // clients
        const perMonth = trends.avgMonthlyNewClients;
        if (perMonth <= 0) {
            return { label: 'Dati insufficienti', detail: 'Non ci sono abbastanza nuovi clienti recenti per una stima.', tone: 'neutral' };
        }
        const months = g.target / perMonth;
        let detail = `Acquisisci in media ~${perMonth.toFixed(1)} nuovi clienti/mese: a questo ritmo raggiungi ${g.target} clienti in circa ${months.toFixed(1)} mesi.`;
        let tone: 'good' | 'warn' | 'neutral' = 'good';
        if (g.deadline) {
            const monthsToDeadline = (new Date(g.deadline).getTime() - Date.now()) / (1000 * 60 * 60 * 24 * 30);
            if (monthsToDeadline > 0) {
                const neededPerMonth = g.target / monthsToDeadline;
                const ok = perMonth >= neededPerMonth;
                tone = ok ? 'good' : 'warn';
                detail += ` Per la scadenza servono ~${neededPerMonth.toFixed(1)}/mese (${ok ? 'in linea' : 'ritmo attuale insufficiente'}).`;
            }
        }
        return { label: months <= 12 ? 'Raggiungibile' : 'Obiettivo di lungo periodo', detail, tone };
    };

    const toneClass = (tone: 'good' | 'warn' | 'neutral') =>
        tone === 'good' ? 'bg-green-50 text-green-700' : tone === 'warn' ? 'bg-amber-50 text-amber-700' : 'bg-gray-50 text-gray-500';

    const selectedGoal = goals.find((g) => g.id === selectedGoalId) || null;

    // Badge di stato mostrato nella riga della lista.
    const goalBadge = (g: Goal) => {
        const base = 'px-2 py-0.5 rounded-full text-[11px] font-bold shrink-0';
        if (g.status === 'completed') return <span className={`${base} bg-green-50 text-green-700`}>Completato</span>;
        if (g.plan_accepted_at) return <span className={`${base} bg-clip-50 text-clip-600`}>Piano attivo</span>;
        if (g.ai_analysis) return <span className={`${base} bg-gray-100 text-gray-500`}>Analisi pronta</span>;
        if (goalPlans[g.id]?.loading) return <span className={`${base} bg-amber-50 text-amber-700 inline-flex items-center gap-1`}><Loader2 size={10} className="animate-spin" /> Analisi…</span>;
        return null;
    };

    // Contenuto del modal di dettaglio: analisi AI, piano e verifiche. Riusa i dati/handler esistenti.
    const renderGoalDetail = (g: Goal) => {
        const proj = projectionFor(g);
        return (
            <div className="space-y-3">
                {/* Il responso locale resta per gli obiettivi clienti; per quelli economici parla l'AI. */}
                {g.type === 'clients' && (
                    <div className={`rounded-2xl p-4 flex items-start gap-3 ${toneClass(proj.tone)}`}>
                        <TrendingUp size={18} className="mt-0.5 shrink-0" />
                        <div>
                            <p className="font-bold text-sm">{proj.label}</p>
                            <p className="text-xs opacity-90 leading-relaxed">{proj.detail}</p>
                        </div>
                    </div>
                )}

                {g.description && <p className="text-sm text-gray-500 italic">{g.description}</p>}

                {/* ===== Piano attivo (accettato) ===== */}
                {g.plan_accepted_at ? (
                    <div className="rounded-2xl border border-gray-100 bg-gray-50/60 p-4 space-y-3">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                            <div className="flex items-center gap-2 text-gray-700 text-xs font-bold uppercase tracking-wider">
                                <Target size={13} className="text-clip-500" /> Piano {g.status === 'completed' ? 'completato 🎉' : 'attivo'}
                            </div>
                            {(() => {
                                const done = checks.filter((c) => c.goal_id === g.id && c.status === 'done').length;
                                const missed = checks.filter((c) => c.goal_id === g.id && c.status === 'missed').length;
                                return (
                                    <span className="text-xs font-bold text-gray-500">
                                        {done}/{g.plan_duration_months ?? '—'} verifiche ok{missed > 0 ? ` · ${missed} mancate` : ''}
                                    </span>
                                );
                            })()}
                        </div>
                        <p className="text-sm text-gray-600">
                            {g.type === 'economic' ? (
                                <>Rata <b>{euro(g.plan_monthly_amount || 0)}/mese</b> per <b>{g.plan_duration_months} mesi</b>
                                    {g.plan_interest_rate ? <> · interessi {g.plan_interest_rate}%</> : null}
                                    {g.plan_final_price ? <> · totale {euro(g.plan_final_price)}</> : null}</>
                            ) : (
                                <>Quota <b>{g.plan_monthly_amount} clienti/mese</b> per <b>{g.plan_duration_months} mesi</b></>
                            )}
                            {g.plan_start_date ? <> · dal {new Date(g.plan_start_date).toLocaleDateString('it-IT')}</> : null}
                        </p>
                        {g.status !== 'completed' && g.next_check_date && (
                            new Date(g.next_check_date) <= new Date() ? (
                                <div className="rounded-xl bg-amber-50 border border-amber-100 p-3">
                                    <p className="text-sm font-bold text-amber-800">
                                        Verifica del periodo: hai rispettato l'impegno{g.type === 'economic' ? ' (es. rata pagata)' : ` (${g.plan_monthly_amount} clienti acquisiti)`}?
                                    </p>
                                    <div className="flex gap-2 mt-2">
                                        <button onClick={() => confirmCheck(g, 'done')} className="px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-xs font-bold transition-colors">
                                            ✓ Sì, rispettato
                                        </button>
                                        <button onClick={() => confirmCheck(g, 'missed')} className="px-3 py-1.5 rounded-lg bg-white border border-red-200 text-red-600 hover:bg-red-50 text-xs font-bold transition-colors">
                                            ✗ Non rispettato
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-xs text-gray-400">Prossima verifica: {new Date(g.next_check_date).toLocaleDateString('it-IT')}</p>
                            )
                        )}
                        {/* Torna all'analisi AI per modificare i parametri e riattivare l'obiettivo. */}
                        <div className="pt-1 border-t border-gray-100 flex justify-end">
                            <button onClick={() => reopenPlan(g)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-clip-200 text-clip-600 hover:bg-clip-50 font-bold text-xs transition-colors">
                                <Pencil size={13} /> Riconfigura piano
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* ===== Analisi/piano AI (automatica alla creazione, persistita) ===== */}
                        {(() => {
                            const st = goalPlans[g.id];
                            const persisted = g.ai_analysis as unknown as PlanProposal | null;
                            const plan = st?.plan ?? persisted;
                            if (st?.loading) {
                                return (
                                    <div className="rounded-2xl border border-clip-100 bg-clip-50/50 p-4 flex items-center gap-2 text-sm text-gray-500">
                                        <Loader2 size={16} className="animate-spin text-clip-500" /> L'AI sta analizzando l'obiettivo sui dati del salone…
                                    </div>
                                );
                            }
                            if (plan) {
                                const fatt = (plan.fattibile || '').toLowerCase();
                                const fattClass = fatt === 'si' || fatt === 'sì' ? 'bg-green-50 text-green-700' : fatt === 'no' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700';
                                return (
                                    <div className="rounded-2xl border border-clip-100 bg-clip-50/50 p-4 space-y-3">
                                        <div className="flex items-center justify-between flex-wrap gap-2">
                                            <div className="flex items-center gap-2 text-clip-600 text-xs font-bold uppercase tracking-wider">
                                                <Sparkles size={13} /> Analisi AI
                                            </div>
                                            <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${fattClass}`}>
                                                {fatt === 'si' || fatt === 'sì' ? 'Fattibile' : fatt === 'no' ? 'Sconsigliato ora' : 'Fattibile con condizioni'}
                                            </span>
                                        </div>
                                        {plan.analisi && <div className="text-sm text-gray-700"><MarkdownLite text={plan.analisi} /></div>}
                                        {(plan.durata_mesi != null || plan.rata_mensile_eur != null) && (
                                            <div className="grid grid-cols-3 gap-2 text-center">
                                                {plan.rata_mensile_eur != null && (
                                                    <div className="rounded-xl bg-white py-2.5 border border-gray-100">
                                                        <p className="text-[10px] font-bold text-gray-400 uppercase">{g.type === 'economic' ? 'Rata consigliata' : 'Quota mensile'}</p>
                                                        <p className="font-bold text-gray-900 text-sm">{g.type === 'economic' ? `${euro(plan.rata_mensile_eur)}/mese` : `${plan.rata_mensile_eur}/mese`}</p>
                                                    </div>
                                                )}
                                                {plan.durata_mesi != null && (
                                                    <div className="rounded-xl bg-white py-2.5 border border-gray-100">
                                                        <p className="text-[10px] font-bold text-gray-400 uppercase">Durata</p>
                                                        <p className="font-bold text-gray-900 text-sm">{plan.durata_mesi} mesi</p>
                                                    </div>
                                                )}
                                                {plan.quota_margine_pct != null && (
                                                    <div className="rounded-xl bg-white py-2.5 border border-gray-100">
                                                        <p className="text-[10px] font-bold text-gray-400 uppercase">Quota margine</p>
                                                        <p className="font-bold text-gray-900 text-sm">{plan.quota_margine_pct}%</p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        {Array.isArray(plan.consigli) && plan.consigli.length > 0 && (
                                            <ul className="space-y-1">
                                                {plan.consigli.map((c, i) => (
                                                    <li key={i} className="text-xs text-gray-600 flex gap-1.5"><span className="text-clip-500">•</span>{c}</li>
                                                ))}
                                            </ul>
                                        )}
                                        <div className="flex justify-end items-center gap-3 pt-1">
                                            <button onClick={() => setEditingGoal(g)} disabled={goalPlans[g.id]?.loading} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-clip-200 text-clip-600 hover:bg-clip-50 font-bold text-sm transition-colors disabled:opacity-50">
                                                <Pencil size={14} /> Modifica obiettivo
                                            </button>
                                            {!planEdit[g.id] && (
                                                <button onClick={() => openPlanEditor(g)} className="px-4 py-2 rounded-xl bg-clip-500 hover:bg-clip-600 text-white font-bold text-sm transition-colors">
                                                    Accetta e configura
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            }
                            if (st?.text) {
                                return (
                                    <div className="rounded-2xl border border-clip-100 bg-clip-50/50 p-4">
                                        <div className="flex items-center gap-2 text-clip-600 text-xs font-bold uppercase tracking-wider mb-2">
                                            <Sparkles size={13} /> Analisi AI
                                        </div>
                                        <div className="text-sm text-gray-700"><MarkdownLite text={st.text} /></div>
                                        <div className="flex items-center gap-3 mt-2">
                                            {!planEdit[g.id] && (
                                                <button onClick={() => openPlanEditor(g)} className="text-xs font-bold text-clip-600 hover:underline">Configura piano manualmente</button>
                                            )}
                                            <button onClick={() => setEditingGoal(g)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-clip-200 text-clip-600 hover:bg-clip-50 font-bold text-xs transition-colors">
                                                <Pencil size={13} /> Modifica obiettivo
                                            </button>
                                        </div>
                                    </div>
                                );
                            }
                            return (
                                <button
                                    onClick={() => analyzeGoal(g)}
                                    className="flex items-center gap-2 px-4 py-2 rounded-xl border border-clip-200 text-clip-600 hover:bg-clip-50 font-bold text-sm transition-colors"
                                >
                                    <Sparkles size={15} /> Valuta con AI
                                </button>
                            );
                        })()}

                        {/* ===== Configurazione parametri piano ===== */}
                        {planEdit[g.id] && (
                            <div className="rounded-2xl border border-gray-200 bg-white p-4 space-y-3">
                                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Configura il piano</p>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    {g.type === 'economic' && (
                                        <div>
                                            <label className="text-[10px] font-bold text-gray-400 uppercase">Prezzo finale (€)</label>
                                            <input type="number" value={planEdit[g.id]!.final_price} onChange={(e) => updatePlanField(g, 'final_price', e.target.value)} className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-clip-200 focus:border-clip-400 outline-none font-medium transition-all placeholder:text-gray-400 text-gray-900" />
                                        </div>
                                    )}
                                    <div>
                                        <label className="text-[10px] font-bold text-gray-400 uppercase">{g.type === 'economic' ? 'Rata mensile (€)' : 'Clienti al mese'}</label>
                                        <input type="number" value={planEdit[g.id]!.monthly} onChange={(e) => updatePlanField(g, 'monthly', e.target.value)} className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-clip-200 focus:border-clip-400 outline-none font-medium transition-all placeholder:text-gray-400 text-gray-900" />
                                    </div>
                                    {g.type === 'economic' && (
                                        <div>
                                            <label className="text-[10px] font-bold text-gray-400 uppercase">Interessi (%)</label>
                                            <input type="number" step="0.1" value={planEdit[g.id]!.interest} onChange={(e) => updatePlanField(g, 'interest', e.target.value)} className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-clip-200 focus:border-clip-400 outline-none font-medium transition-all placeholder:text-gray-400 text-gray-900" />
                                        </div>
                                    )}
                                    <div>
                                        <label className="text-[10px] font-bold text-gray-400 uppercase">Durata (mesi)</label>
                                        <input type="number" value={planEdit[g.id]!.months} onChange={(e) => updatePlanField(g, 'months', e.target.value)} className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-clip-200 focus:border-clip-400 outline-none font-medium transition-all placeholder:text-gray-400 text-gray-900" />
                                    </div>
                                    <div className="col-span-2">
                                        <label className="text-[10px] font-bold text-gray-400 uppercase">Inizio</label>
                                        <DatePicker
                                            selected={planEdit[g.id]!.start ? new Date(`${planEdit[g.id]!.start}T00:00:00`) : undefined}
                                            onSelect={(d) => updatePlanField(g, 'start', d ? toIso(d) : '')}
                                            allowClosedDays
                                            placeholder="Scegli una data"
                                        />
                                    </div>
                                </div>
                                {/* Riepilogo del legame importo ↔ rata ↔ durata: cambia un valore e gli altri si adeguano. */}
                                {(() => {
                                    const e = planEdit[g.id]!;
                                    const monthly = parseFloat(e.monthly) || 0;
                                    const months = parseInt(e.months) || 0;
                                    if (monthly <= 0 || months <= 0) return null;
                                    return g.type === 'economic' ? (
                                        <p className="text-xs text-gray-500">
                                            {euro(monthly)}/mese × {months} mesi = <b className="text-gray-700">{euro(monthly * months)}</b>
                                            {planTotalFor(g, e) > 0 && <span className="text-gray-400"> (totale con interessi)</span>}
                                        </p>
                                    ) : (
                                        <p className="text-xs text-gray-500">
                                            {monthly} clienti/mese × {months} mesi = <b className="text-gray-700">{monthly * months} clienti</b>
                                            <span className="text-gray-400"> (obiettivo: {g.target})</span>
                                        </p>
                                    );
                                })()}
                                <div className="flex items-center gap-2 justify-end">
                                    <button onClick={() => setPlanEdit((p) => ({ ...p, [g.id]: undefined }))} className="px-4 py-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 font-bold text-sm transition-colors">Annulla</button>
                                    <button onClick={() => savePlan(g)} className="px-4 py-2 rounded-xl bg-clip-500 hover:bg-clip-600 text-white font-bold text-sm transition-colors">Conferma piano</button>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {g.note && <p className="text-xs text-gray-400">📝 {g.note}</p>}
            </div>
        );
    };

    const tabBtn = (active: boolean) =>
        `flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all ${active ? 'bg-clip-500 text-white shadow-md shadow-clip-200' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'}`;

    return (
        <motion.div className="max-w-7xl mx-auto space-y-6 pb-20 md:pb-0" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {/* HEADER */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Assistente AI</h1>
                    <p className="text-gray-500 mt-1">Chatta con l'assistente e gestisci gli obiettivi del salone</p>
                </div>
                {activeTab === 'goals' && (
                    <PrimaryButton
                        label="Nuovo obiettivo"
                        icon={Plus}
                        variant="primary"
                        showLabelOnMobile
                        onClick={() => setIsNewGoalOpen(true)}
                    />
                )}
            </div>

            {/* TABS */}
            <div className="bg-white rounded-2xl p-1.5 border border-gray-100 shadow-sm inline-flex gap-1">
                <button onClick={() => setActiveTab('chat')} className={tabBtn(activeTab === 'chat')}>
                    <MessageSquare size={16} /> Assistente
                </button>
                <button onClick={() => setActiveTab('goals')} className={tabBtn(activeTab === 'goals')}>
                    <Target size={16} /> Obiettivi
                </button>
            </div>

            {activeTab === 'chat' ? (
                /* ===== TAB CHATBOT ===== */
                <div className="w-full bg-white rounded-3xl border border-gray-100 shadow-sm p-4 sm:p-5 flex flex-col h-[calc(100dvh-17rem)] min-h-[30rem] overflow-hidden">
                    <div className="mb-4 shrink-0">
                        <h2 className="text-base font-bold text-gray-900 leading-none">Assistente AI</h2>
                        <p className="text-xs text-gray-400 mt-1">Domande sui dati del tuo salone</p>
                    </div>
                    <div className="flex-1 min-h-0">
                        <AssistantChat />
                    </div>
                </div>
            ) : (
                /* ===== TAB OBIETTIVI ===== */
                <div className="space-y-4">
                    {loading ? (
                        <div className="flex items-center gap-2 text-gray-400 p-6 justify-center">
                            <Loader2 size={18} className="animate-spin" /> Carico gli obiettivi…
                        </div>
                    ) : goals.length === 0 ? (
                        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-10 text-center">
                            <div className="w-14 h-14 rounded-2xl bg-clip-50 text-clip-500 flex items-center justify-center mx-auto mb-3">
                                <Target size={26} />
                            </div>
                            <p className="font-bold text-gray-900">Nessun obiettivo</p>
                            <p className="text-sm text-gray-400 mt-1 max-w-sm mx-auto">Crea il tuo primo obiettivo: l'AI lo analizza sui tuoi dati e ti propone un piano.</p>
                            <div className="mt-4 flex justify-center">
                                <PrimaryButton label="Nuovo obiettivo" icon={Plus} variant="primary" showLabelOnMobile onClick={() => setIsNewGoalOpen(true)} />
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-2.5">
                            {goals.map((g) => (
                                <div
                                    key={g.id}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => setSelectedGoalId(g.id)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') setSelectedGoalId(g.id); }}
                                    className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-4 hover:border-clip-200 hover:shadow transition-all cursor-pointer group"
                                >
                                    <div className="w-11 h-11 rounded-xl bg-clip-50 text-clip-600 flex items-center justify-center shrink-0">
                                        {g.type === 'economic' ? <Euro size={18} /> : <Users size={18} />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <p className="font-bold text-gray-900 truncate">
                                                {g.title || (g.type === 'economic' ? 'Obiettivo economico' : 'Obiettivo clienti')}
                                            </p>
                                            {goalBadge(g)}
                                        </div>
                                        <p className="text-sm text-gray-500 mt-0.5">
                                            {g.type === 'economic' ? euro(g.target) : `${g.target} clienti`}
                                            {g.deadline ? ` · entro il ${new Date(g.deadline).toLocaleDateString('it-IT')}` : ''}
                                        </p>
                                    </div>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleDelete(g.id); }}
                                        title="Elimina obiettivo"
                                        className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors shrink-0"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                    <ChevronRight size={18} className="text-gray-300 group-hover:text-clip-500 transition-colors shrink-0" />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* MODAL: nuovo obiettivo */}
            <GoalFormModal
                isOpen={isNewGoalOpen}
                onClose={() => setIsNewGoalOpen(false)}
                onCreate={handleCreate}
                saving={isSaving}
            />

            {/* MODAL: modifica obiettivo (rigenera l'analisi AI al salvataggio) */}
            <GoalFormModal
                isOpen={!!editingGoal}
                onClose={() => setEditingGoal(null)}
                onCreate={handleEditGoal}
                saving={isSaving}
                initial={editingGoal && {
                    type: editingGoal.type,
                    title: editingGoal.title || '',
                    target: editingGoal.target,
                    deadline: editingGoal.deadline,
                    description: editingGoal.description,
                }}
            />

            {/* MODAL: dettaglio obiettivo + consigli AI */}
            <Modal
                isOpen={!!selectedGoal}
                onClose={() => setSelectedGoalId(null)}
                className="max-w-2xl"
                headerClassName="bg-white border-b border-gray-100"
                headerContent={selectedGoal && (
                    <div className="flex items-center gap-3 pr-8">
                        <div className="w-12 h-12 rounded-2xl bg-clip-50 text-clip-600 flex items-center justify-center shrink-0">
                            {selectedGoal.type === 'economic' ? <Euro size={20} /> : <Users size={20} />}
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-xl font-bold text-gray-900 truncate">
                                {selectedGoal.title || (selectedGoal.type === 'economic' ? 'Obiettivo economico' : 'Obiettivo clienti')}
                            </h2>
                            <p className="text-sm text-gray-500">
                                {selectedGoal.type === 'economic' ? euro(selectedGoal.target) : `${selectedGoal.target} clienti`}
                                {selectedGoal.deadline ? ` · entro il ${new Date(selectedGoal.deadline).toLocaleDateString('it-IT')}` : ''}
                            </p>
                        </div>
                    </div>
                )}
            >
                {selectedGoal && renderGoalDetail(selectedGoal)}
            </Modal>
        </motion.div>
    );
};

export default Assistant;
